import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useQuery, gql } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { Box, CircularProgress, Stack } from '@mui/material';
import RankingTable from './RankingTable';
import EmptyResultBlock from '../shared/blocks/EmptyResultsBlock';

// GraphQL Query
const GLOBAL_LEADERBOARD = gql`
  query GlobalLeaderboard($limit: Int!, $offset: Int!) {
    globalLeaderboard(limit: $limit, offset: $offset) {
      leaderboard {
        position
        user {
          id
          name
          profile_photo_path
          gender
        }
        total_points
        monthly_base_score
        monthly_total_score
        mythological_rank
        mythological_rank_info {
          name
          name_en
          description
          description_en
          color
          icon
        }
      }
      total
      my_position
    }
  }
`;

/**
 * GlobalLeaderboard - Tabla completa del leaderboard con infinite scroll
 */
function GlobalLeaderboard() {
  const { t } = useTranslation(['common']);
  const userId = useSelector((state) => state.auth.user.id);
  const [allEntries, setAllEntries] = useState([]);
  const [hasMore, setHasMore] = useState(true);

  const { loading, error, data, fetchMore } = useQuery(GLOBAL_LEADERBOARD, {
    variables: { limit: 20, offset: 0 },
    fetchPolicy: 'cache-and-network',
    onCompleted: (responseData) => {
      const entries = responseData.globalLeaderboard.leaderboard.filter(
        (e) => (e.total_points ?? 0) > 0
      );
      const sorted = [...entries].sort((a, b) => (b.total_points ?? 0) - (a.total_points ?? 0));
      const withPosition = sorted.map((e, i) => ({ ...e, position: i + 1 }));
      setAllEntries(withPosition);
      setHasMore(entries.length < responseData.globalLeaderboard.total);
    },
  });

  const loadMore = useCallback(async () => {
    if (!hasMore || loading) return;

    try {
      const { data: newData } = await fetchMore({
        variables: {
          limit: 20,
          offset: allEntries.length,
        },
      });

      const newEntries = newData.globalLeaderboard.leaderboard.filter(
        (e) => (e.total_points ?? 0) > 0
      );
      const combined = [...allEntries, ...newEntries].sort((a, b) => (b.total_points ?? 0) - (a.total_points ?? 0));
      const withPosition = combined.map((e, i) => ({ ...e, position: i + 1 }));
      setAllEntries(withPosition);
      setHasMore(combined.length < newData.globalLeaderboard.total);
    } catch (err) {
      console.error('Error loading more:', err);
    }
  }, [hasMore, loading, fetchMore, allEntries]);

  // Infinite scroll handler
  const handleScroll = useCallback(() => {
    if (
      window.innerHeight + document.documentElement.scrollTop
      >= document.documentElement.offsetHeight - 100
    ) {
      loadMore();
    }
  }, [loadMore]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  if (loading && allEntries.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <Stack spacing={2} alignItems="center">
          <CircularProgress sx={{ color: 'var(--otx-gold-text)' }} />
          <Box sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
            Cargando ranking...
          </Box>
        </Stack>
      </Box>
    );
  }

  if (error) {
    const emptyTitle = t('emptyStates.ranking.title', { ns: 'common' });
    const emptyMessage = t('emptyStates.ranking.message', { ns: 'common' });

    return (
      <Box sx={{ borderRadius: 3, p: 6 }}>
        <EmptyResultBlock
          imageType="woman"
          imageHeight="150px"
          message={`${emptyTitle}\n${emptyMessage}`}
        />
      </Box>
    );
  }

  const myPosition = data?.globalLeaderboard.my_position;
  const total = data?.globalLeaderboard.total || 0;

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
      {/* Header con mi posición */}
      {myPosition && (
        <Box
          sx={{
            backgroundColor: 'surface.overlay',
            border: '1px solid',
            borderColor: 'hairline.main',
            borderRadius: 2,
            p: 2.5,
            mb: 3,
            textAlign: 'center',
          }}
        >
          <Box sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
            <Box component="span" sx={{ fontWeight: 700, color: 'text.primary', mr: 0.5 }}>
              Tu posición actual:
            </Box>
            <Box component="span" sx={{ fontWeight: 800, color: 'var(--otx-gold-text)' }}>
              #{myPosition}
            </Box>{' '}
            de {total.toLocaleString()} atletas
          </Box>
        </Box>
      )}

      {/* Tabla del leaderboard */}
      {allEntries.length > 0 ? (
        <RankingTable entries={allEntries} currentUserId={userId} />
      ) : (
        <Box sx={{ borderRadius: 3, p: 6 }}>
          <EmptyResultBlock
            imageType="woman"
            imageHeight="150px"
            message={`${t('emptyStates.ranking.title', { ns: 'common' })}\n${t('emptyStates.ranking.message', { ns: 'common' })}`}
          />
        </Box>
      )}

      {/* Loading more indicator */}
      {loading && allEntries.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={32} sx={{ color: 'var(--otx-gold-text)' }} />
        </Box>
      )}

      {/* No more results */}
      {!hasMore && allEntries.length > 0 && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Box sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
            {t('ranking.endOfList', { ns: 'common', defaultValue: 'Has llegado al final del ranking' })}
          </Box>
        </Box>
      )}
    </Box>
  );
}

export default GlobalLeaderboard;
